import { REGEX } from 'constants/validation/regexes';
import { useLocalization } from 'localization';
import { array, boolean, object, string } from 'yup';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export const taskTextSchema = () => {
  const { t } = useLocalization();

  return object().shape({
    importantElement: string().required(t('validation.mixed.required')),
    property: object().shape({
      type: string().required(t('validation.mixed.required')),
    }),
    writingStyle: object().shape({
      type: string(),
    }),
    responsibleAgent: string().required(t('validation.mixed.required')),
    contactPerson: string().when('isInterview', {
      is: true,
      then: schema => schema.required(t('validation.mixed.required')),
    }),
    contactPersonName: string().when('isInterview', {
      is: true,
      then: schema => schema.required(t('validation.mixed.required')),
    }),
    phone: string().when('isInterview', {
      is: true,
      then: schema =>
        schema.required(t('validation.mixed.required')).test('phonePossible', t('validation.mixed.default'), val => {
          if (!val) return true;
          const p = parsePhoneNumberFromString(val);
          return !!p && p.isPossible() && p.isValid();
        }),
    }),
    email: string()
      .email()
      .when('isInterview', {
        is: true,
        then: schema =>
          schema.matches(REGEX.EMAIL, t('validation.mixed.default')).required(t('validation.mixed.required')),
      }),
    phoneAlt: string().when('isInterview', {
      is: true,
      then: schema =>
        schema.test('phonePossible', t('validation.mixed.default'), val => {
          if (!val) return true;
          const p = parsePhoneNumberFromString(val);
          return !!p && p.isPossible() && p.isValid();
        }),
    }),
    isInterview: boolean().default(false),
  });
};

export const taskAITextInputSchema = () => {
  const { t } = useLocalization();

  return object().shape({
    importantElement: string().required(t('validation.mixed.required')),
    property: object().shape({
      type: string().required(t('validation.mixed.required')),
    }),
    responsibleAgent: string().required(t('validation.mixed.required')),
    contactPerson: string().when('isQuestionnaire', {
      is: true,
      then: schema => schema.required(t('validation.mixed.required')),
    }),
    contactPersonName: string().when('isQuestionnaire', {
      is: true,
      then: schema => schema.required(t('validation.mixed.required')),
    }),
    email: string()
      .email()
      .nullable()
      .when('isQuestionnaire', {
        is: true,
        then: schema =>
          schema.matches(REGEX.EMAIL, t('validation.mixed.default')).required(t('validation.mixed.required')),
      }),
    isQuestionnaire: boolean().default(false),
  });
};

export const inputBasicSchema = () => {
  const { t } = useLocalization();
  return object().shape({
    instructions: string().required(t('validation.mixed.required')),
  });
};

export const personInformationSchema = () => {
  const { t } = useLocalization();
  return object().shape({
    name: string().required(t('validation.mixed.required')),
    email: string()
      .email()
      .matches(REGEX.EMAIL, t('validation.mixed.default'))
      .required(t('validation.mixed.required')),
    phone: string()
      .required(t('validation.mixed.required'))
      .test('phonePossible', t('validation.mixed.default'), val => {
        if (!val) return true;
        const p = parsePhoneNumberFromString(val);
        return !!p && p.isPossible() && p.isValid();
      }),
    comment: string().default(''),
  });
};

export const vilibidiCommentSchema = () => {
  const { t } = useLocalization();
  return object().shape({
    companyName: string().required(t('validation.mixed.required')),
    comment: string().required(t('validation.mixed.required')),
    website: string().default(''),
    address: string().default(''),
    companyDescription: string().default(''),
    unsubcribeDescription: string().default(''),
  });
};

export const textSectionSchema = () => {
  const { t } = useLocalization();

  return object().shape({
    sections: array()
      .of(
        object().shape({
          sectionName: string().required(t('validation.mixed.required')),
          content: string().required(t('validation.mixed.required')),
        })
      )
      .min(1, t('validation.array.min', { min: 1 })),

    isTriggeringReject: boolean().default(false),

    complaintFields: object({
      text: string().default(''),
      option: string().default(''),
    }).when('isTriggeringReject', {
      is: true,
      then: schema =>
        schema.shape({
          text: string().required(t('validation.mixed.required')),
          option: string().required(t('validation.mixed.required')),
        }),
      otherwise: schema => schema.notRequired(),
    }),
  });
};

export const personSchema = () => {
  const { t } = useLocalization();
  return object().shape({
    names: string().required(t('validation.mixed.required')),
    comment: string().default(''),
  });
};

export const introOutSpeechSchema = () => {
  const { t } = useLocalization();
  return object().shape({
    input: string().required(t('validation.mixed.required')),
  });
};

export const speakInputSchema = () => {
  const { t } = useLocalization();
  return object().shape({
    speakInput: string().required(t('validation.mixed.required')),
  });
};

export const comTextSchema = () => {
  const { t } = useLocalization();

  return object().shape({
    textForCopyWriter: string().required(t('validation.mixed.required')),
    property: object().shape({
      type: string().required(t('validation.mixed.required')),
    }),
    offerForm: object().shape({
      type: string(),
    }),
    responsibleAgent: string().required(t('validation.mixed.required')),
    contactPerson: string().when('isInterview', {
      is: true,
      then: schema => schema.required(t('validation.mixed.required')),
    }),
    contactPersonName: string().when('isInterview', {
      is: true,
      then: schema => schema.required(t('validation.mixed.required')),
    }),
    phone: string().when('isInterview', {
      is: true,
      then: schema =>
        schema.required(t('validation.mixed.required')).test('phonePossible', t('validation.mixed.default'), val => {
          if (!val) return true;
          const p = parsePhoneNumberFromString(val);
          return !!p && p.isPossible() && p.isValid();
        }),
    }),
    email: string()
      .email()
      .when('isInterview', {
        is: true,
        then: schema => schema.required(t('validation.mixed.required')),
      }),
    phoneAlt: string().when('isInterview', {
      is: true,
      then: schema => schema.required(t('validation.mixed.required')),
    }),
    isInterview: boolean().default(false),
  });
};

export const aerialPhotoSchema = () => {
  const { t } = useLocalization();
  return object().shape({
    roofColor: string().required(t('validation.mixed.required')),
    cadastralNumber: string().required(t('validation.mixed.required')),
    comment: string(),
  });
};
